
'use client';

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Hero } from '@/components/Hero';
import { Projects } from '@/components/Projects';
import { PriceCard } from '@/components/PriceCard';
import { Button } from '@/components/ui/button';
import { Link } from '@/navigation';
import { 
  Zap, ShieldCheck, Crown, ChevronRight, Phone, Send
} from 'lucide-react';
import { WhyUsCodeBlock } from '@/components/WhyUsCodeBlock';

export default function Home() {
  const t = useTranslations('hero');
  const procT = useTranslations('process');
  const priceT = useTranslations('pricing');
  const ctaT = useTranslations('cta');
  const marqueeT = useTranslations('marquee');

  const [showContactOptions, setShowContactOptions] = useState(false);

  const processSteps = procT.raw('steps');

  return (
    <main className="min-h-screen bg-white dark:bg-black font-sans selection:bg-black selection:text-white dark:selection:bg-white dark:selection:text-black">
      <Navbar />
      
      {/* HERO SECTION */}
      <Hero />

      {/* MARQUEE */}
      <div className="border-y border-gray-100 py-10 overflow-hidden bg-white dark:bg-black dark:border-gray-900">
        <div className="flex animate-marquee whitespace-nowrap">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-center gap-12 px-6">
              <span className="text-2xl md:text-4xl font-bold uppercase tracking-tighter text-gray-200 dark:text-zinc-800 hover:text-black dark:hover:text-white transition-colors duration-500 cursor-default">
                {marqueeT('text')}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* PROJECTS PREVIEW */}
      <Projects />

      {/* WHY US - CODE BLOCK SECTION */}
      <WhyUsCodeBlock />

      {/* PROCESS */}
      <section className="py-32 bg-gray-50 dark:bg-zinc-950">
        <div className="container mx-auto px-6">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-6xl font-black tracking-tight mb-20 text-center text-black dark:text-white uppercase font-canva"
          >
            {procT('title')}
          </motion.h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12">
            {processSteps.map((step: any, i: number) => (
              <motion.div 
                key={i} 
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="flex flex-col items-center text-center group"
              >
                <div className="w-16 h-16 bg-black text-white flex items-center justify-center rounded-2xl mb-8 text-xl font-bold dark:bg-white dark:text-black group-hover:rotate-6 transition-transform duration-300">
                  {i + 1}
                </div>
                <h3 className="text-xl font-bold mb-4 dark:text-white">{step.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed dark:text-gray-400 max-w-xs">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="py-32 bg-white dark:bg-black">
        <div className="container mx-auto px-6">
          <div className="text-center mb-24">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl md:text-6xl font-black tracking-tight mb-8 text-black dark:text-white uppercase font-canva"
            >
              {priceT('title')}
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-xl text-gray-500 dark:text-gray-400 mb-10 max-w-2xl mx-auto"
            >
              {priceT('desc')}
            </motion.p>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-full text-amber-700 text-sm font-medium dark:bg-amber-900/20 dark:border-amber-900/30 dark:text-amber-400">
              {priceT('warning_pill')}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto">
            <PriceCard plan={priceT.raw('plans.landing')} icon={Zap} btnLabel={priceT('cta_btn')} />
            <PriceCard plan={priceT.raw('plans.multi')} icon={ShieldCheck} highlighted btnLabel={priceT('cta_btn')} />
            <PriceCard plan={priceT.raw('plans.premium')} icon={Crown} btnLabel={priceT('cta_btn')} />
          </div>

          <div className="text-center mt-16">
            <Button asChild variant="link" className="text-black dark:text-white text-lg h-auto hover:gap-4 transition-all">
              <Link href="/pricing" className="flex items-center gap-2">
                Barcha tafsilotlarni ko'rish <ChevronRight size={20} />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-32 bg-gray-50 dark:bg-zinc-950">
        <div className="container mx-auto px-6 max-w-4xl">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-6xl font-black tracking-tighter mb-16 text-center text-black dark:text-white uppercase font-canva"
          >
            {priceT('faq.title')}
          </motion.h2>
          <div className="space-y-4">
            {priceT.raw('faq.items').map((item: any, i: number) => (
              <FAQItem key={i} question={item.q} answer={item.a} />
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}

function FAQItem({ question, answer }: { question: string, answer: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-gray-200 dark:border-zinc-800 last:border-0">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        className="w-full py-6 flex items-center justify-between text-left hover:opacity-70 transition-opacity"
      >
        <span className="text-lg font-bold dark:text-white">{question}</span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3 }}
          className="text-gray-500 dark:text-gray-400"
        >
          <ChevronRight size={20} />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <p className="pb-6 text-gray-500 dark:text-gray-400 leading-relaxed">
              {answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
